import { css } from "@emotion/css";
import { $, map, type Properties, resolve } from "signlets";

import {
  calculateBestGlideSpeed,
  calculateCenterOfGravity,
  calculateMaxCenterOfGravity,
  calculateMinCenterOfGravity,
  calculateWeight,
} from "../calculations";
import { range } from "../common";
import { minimumWeight } from "../configuration";
import { metersPerNauticalMile } from "../constants";
import type { Cargo, Drop } from "../model";
import { Button } from "./Button";
import { Field } from "./Field";
import { H1 } from "./H1";
import { H2 } from "./H2";
import { AddIcon } from "./icons/AddIcon";
import { CloseIcon } from "./icons/CloseIcon";
import { Measurement } from "./Measurement";
import { Modal } from "./Modal";
import { NumberInput } from "./NumberInput";
import { P } from "./P";
import { TextInput } from "./TextInput";

const style = css`
  width: min-content;

  > form {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    > .columns {
      display: grid;
      grid-template-columns: min-content min-content;
      gap: 4rem;

      > .details {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        > .empty {
          width: 8rem;
        }
      }

      > .cargo {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        > .title {
          display: flex;
          gap: 1rem;
        }

        > .list {
          display: grid;
          grid-template-columns: 1fr 8rem 8rem min-content;
          align-items: center;
          gap: 1rem;
        }
      }
    }

    .invalid {
      color: #fa4d56;
    }

    .valid {
      color: #3ac464;
    }

    input[type="range"] {
      width: 100%;
      height: 1rem;
      margin: 0.5rem 0 0;
      appearance: none;
      background: transparent;
      cursor: default;

      &::-webkit-slider-runnable-track {
        height: 0.5rem;
        border: none;
        border-radius: 0.25rem;
        background: #fff3;
      }

      &::-webkit-slider-thumb {
        width: 1rem;
        height: 1rem;
        margin-top: -0.25rem;
        appearance: none;
        border-radius: 50%;
        background: white;
      }
    }

    > button {
      width: max-content;
      min-width: 10rem;
      margin-top: auto;
    }
  }
`;

export type WeightBalanceProps = {
  visible: boolean;
  drop: Drop;
  onChangeDrop: (_: Partial<Drop>) => void;
  onNext: () => void;
};

export const WeightBalance = ({
  drop,
  visible,
  onChangeDrop,
  onNext,
}: Properties<WeightBalanceProps>) => {
  const emptyWeight = $(() => resolve(drop).emptyWeight);
  const onChangeEmptyWeight = (emptyWeight: number) =>
    onChangeDrop({ emptyWeight });

  const cargo = $(() => resolve(drop).cargo);
  const onChangeCargo = (cargo: Cargo[]) => onChangeDrop({ cargo });

  const weight = $(() => calculateWeight(resolve(drop)));
  const centerOfGravity = $(() => calculateCenterOfGravity(resolve(drop)));
  const bestGlideSpeed = $(
    () =>
      calculateBestGlideSpeed(resolve(drop)) / (metersPerNauticalMile / 3600),
  );

  const validWeight = $(() => weight() >= minimumWeight);
  const validCenterOfGravity = $(
    () =>
      centerOfGravity() > calculateMinCenterOfGravity(resolve(drop)) &&
      centerOfGravity() < calculateMaxCenterOfGravity(resolve(drop)),
  );

  const valid = $(() => validWeight() && validCenterOfGravity());

  const onSubmit = (event: Event) => {
    event.preventDefault();
    if (!valid()) return;
    onNext();
  };

  return (
    <Modal class={style} visible={visible}>
      <form onSubmit={onSubmit}>
        <H1>Weight & balance</H1>
        <P>
          Specify cargo weight and location to verify center of gravity and
          mission feasibility.
        </P>
        <div class="columns">
          <div class="details">
            <Field label="Empty weight (lb)" class="empty">
              <NumberInput
                number={emptyWeight()}
                min={400}
                max={1000}
                step={0.1}
                onChangeNumber={onChangeEmptyWeight}
              />
            </Field>
            <Measurement
              label="Total weight"
              value={$(() => weight().toFixed(0))}
              unit="lb"
              class={$(() => (validWeight() ? "valid" : "invalid"))}
            />
            <div
              class={$(() => (validCenterOfGravity() ? "valid" : "invalid"))}
            >
              <Measurement
                label="Center of gravity"
                value={$(() => centerOfGravity().toFixed(1))}
                unit="in"
              />
              <input
                type="range"
                min={$(() =>
                  calculateMinCenterOfGravity(resolve(drop)).toFixed(2),
                )}
                max={$(() =>
                  calculateMaxCenterOfGravity(resolve(drop)).toFixed(2),
                )}
                value={$(() => centerOfGravity().toFixed(2))}
                step="0.01"
                disabled
              />
            </div>
            <Measurement
              label="Best glide speed"
              value={$(() => bestGlideSpeed().toFixed(1))}
              unit="kt"
            />
          </div>
          <CargoList cargo={cargo} onChange={onChangeCargo} />
        </div>
        <Button primary onClick={onNext} disabled={$(() => !valid())}>
          Next
        </Button>
      </form>
    </Modal>
  );
};

type CargoListProps = {
  cargo: Cargo[];
  onChange: (_: Cargo[]) => void;
};

const CargoList = ({ cargo, onChange }: Properties<CargoListProps>) => {
  const onAddCargo = () =>
    onChange([...resolve(cargo), { name: "", weight: 0, location: 0 }]);

  return (
    <div class="cargo">
      <div class="title">
        <H2>Cargo</H2>
        <Button icon title="Add cargo" onClick={onAddCargo}>
          <AddIcon />
        </Button>
      </div>
      <div class="list">
        {map(
          $(() => range(0, resolve(cargo).length)),
          i => {
            const _ = $(
              () => resolve(cargo)[i] ?? { name: "", weight: 0, location: 0 },
            );
            return (
              <CargoLine
                cargo={_}
                onChange={updated =>
                  onChange(
                    resolve(cargo).map((_, j) => (j === i ? updated : _)),
                  )
                }
                onDelete={() =>
                  onChange(resolve(cargo).filter((_, j) => j !== i))
                }
              />
            );
          },
        )}
      </div>
    </div>
  );
};

type CargoLineProps = {
  cargo: Cargo;
  onChange: (_: Cargo) => void;
  onDelete: () => void;
};

const CargoLine = ({
  cargo,
  onChange,
  onDelete,
}: Properties<CargoLineProps>) => {
  const name = $(() => resolve(cargo).name);
  const onChangeName = (name: string) => onChange({ ...resolve(cargo), name });
  const weight = $(() => resolve(cargo).weight);
  const onChangeWeight = (weight: number) =>
    onChange({ ...resolve(cargo), weight });
  const location = $(() => resolve(cargo).location);
  const onChangeLocation = (location: number) =>
    onChange({ ...resolve(cargo), location });

  return (
    <>
      <Field label="Cargo">
        <TextInput value={name} onChangeValue={onChangeName} />
      </Field>
      <Field label="Weight (lb)">
        <NumberInput
          number={weight}
          min={0}
          max={2000}
          step={0.1}
          onChangeNumber={onChangeWeight}
        />
      </Field>
      <Field label="Location (in)">
        <NumberInput
          number={location}
          min={0}
          max={100}
          step={0.01}
          onChangeNumber={onChangeLocation}
        />
      </Field>
      <Button icon title="Delete" onClick={onDelete}>
        <CloseIcon />
      </Button>
    </>
  );
};
